import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  ChevronRight,
  CircleCheck,
  Database,
  KeyRound,
  Layers,
  LayoutDashboard,
  Moon,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Server,
  Sun,
  Trash2,
  TriangleAlert,
  Users,
  X,
} from "lucide-react";
import AccountForm from "./AccountForm";
import useDevTheme from "./useDevTheme";
import ContainerSelect from "./ContainerSelect";
import "./DeveloperDashboard.css";
import "./DeveloperAccounts.css";

/**
 * Account configuration — /dev/accounts.
 *
 * Brokers, the people who hold accounts at them, the machines the bots run on,
 * and the live pair. Changing the pair here applies it immediately: the poller
 * child is recycled and the server is not restarted.
 *
 * THE CURRENT PAIR IS LOCKED UNTIL YOU UNLOCK IT, one click on the pencil. These
 * two fields decide what is being traded; a form that is always editable is a
 * form a stray keystroke can change.
 *
 * IT SHOWS WHAT IS STORED BESIDE WHAT IS RUNNING, because those genuinely can
 * differ — a config saved while the poller was disabled, or a poller that came up
 * from .env because nothing had been imported yet. A page showing only one would
 * imply the other agreed with it.
 *
 * ONE DRILL-DOWN, THREE KINDS. A broker, a person or a bot server all answer the
 * same question — "which accounts?" — so they share one detail view rather than
 * three that would drift apart. Clicking any of them scopes the search to it,
 * which is the point: the bot-IP question is always asked within a scope.
 *
 * DELETING A USER OR A SERVER IS REFUSED WHILE ACCOUNTS USE IT, by the server,
 * counted live. The button is disabled here too, but that is a courtesy — the
 * check that matters is the one that cannot be bypassed by a stale page.
 *
 * No credential reaches this page. The API returns `hasPassword`, never the
 * value — see mt5/accountConfig.js.
 *
 * Shares .dev-root and the palette with DeveloperDashboard.css rather than
 * defining a second one, so the two developer pages cannot drift apart.
 */

const nf = (n) => Number(n || 0).toLocaleString();

/** All terms must match — a second word narrows, as on the dashboard. */
function matches(haystack, terms) {
  for (const t of terms) if (haystack.indexOf(t) === -1) return false;
  return true;
}

const hay = (parts) =>
  parts
    .filter((p) => p !== null && p !== undefined && p !== "")
    .join(" ")
    .toLowerCase();

/**
 * A count, and — when it is given a `go` — the way into what it counted.
 *
 * These tiles do NOT navigate. Everything they count is already listed further
 * down this same page, so a new route would be a second copy of it. They scroll
 * to the section instead, and the two that describe a STATE rather than a kind
 * ("ready", "incomplete") also put that word in the search box: the box already
 * indexes `ready` / `incomplete` per account, so the existing filter does the
 * work and the reason the list narrowed is visible and clearable.
 *
 * A tile with no `go` stays a plain div rather than an inert button, so nothing
 * offers a click that does nothing.
 */
function Tile({ n, label, tone, go }) {
  const cls = `dev-tile${tone ? ` dev-tile-${tone}` : ""}${go ? " dev-tile-link" : ""}`;
  const body = (
    <>
      <span className="dev-tile-n">{nf(n)}</span>
      <span className="dev-tile-l">{label}</span>
      {go && <ChevronRight className="dev-tile-go" size={14} aria-hidden="true" />}
    </>
  );
  if (!go) return <div className={cls}>{body}</div>;
  return (
    <button type="button" className={cls} onClick={go} title={`Show the ${label}`}>
      {body}
    </button>
  );
}

/**
 * Delete, in two clicks, and only when nothing uses the thing.
 *
 * Two clicks rather than a dialog: a dialog for a record with no references is
 * ceremony, and no confirmation at all makes a mis-click silent. The second click
 * is on a button that has visibly changed, so it cannot be the tail of a
 * double-click on the first.
 *
 * It disarms itself after a few seconds. An armed delete button left sitting
 * under the cursor is the thing that gets pressed by accident later.
 */
function DeleteButton({ label, inUse, onDelete, busy }) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);

  if (inUse) {
    return (
      <button
        type="button"
        className="dev-del-btn"
        disabled
        title={`${inUse} account(s) still use this ${label} — change them first`}
        aria-label={`Cannot delete this ${label}: ${inUse} account(s) use it`}
      >
        <Trash2 size={13} aria-hidden="true" />
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`dev-del-btn${armed ? " dev-del-btn-armed" : ""}`}
      disabled={busy}
      onClick={() => (armed ? onDelete() : setArmed(true))}
      title={armed ? "Click again to delete" : `Delete this ${label}`}
      aria-label={armed ? `Confirm deleting this ${label}` : `Delete this ${label}`}
    >
      <Trash2 size={13} aria-hidden="true" />
      {armed && <span>Confirm</span>}
    </button>
  );
}

/** One account, as a card. Used on the overview and in every detail view. */
function AccountCard({ a, onEdit }) {
  return (
    <article className={`dev-card${a.inLivePair ? " dev-card-live" : ""}`}>
      <div className="dev-card-head">
        <h3 className="dev-card-title dev-mono" title={String(a.login)}>
          {a.login}
        </h3>
        <span className="dev-card-head-right">
          {a.inLivePair ? (
            <span className="dev-badge dev-badge-live">current pair</span>
          ) : (
            <span className="dev-badge">past</span>
          )}
          <button
            type="button"
            className="dev-icon-btn dev-icon-btn-sm"
            onClick={() => onEdit(a)}
            aria-label={`Edit ${a.login}`}
            title="Edit"
          >
            <Pencil size={13} aria-hidden="true" />
          </button>
        </span>
      </div>

      <p className="dev-card-text">{a.brokerName || "no broker"}</p>
      <p className="dev-card-when dev-mono-sm">
        {a.host}:{a.port} · {a.platform} · {a.currency}
        {a.leverage ? ` · 1:${a.leverage}` : ""}
      </p>

      <dl className="dev-facts">
        <div>
          <dt>User</dt>
          <dd title={a.userName || ""}>{a.userName || "—"}</dd>
        </div>
        <div>
          <dt>Bot IP</dt>
          {/* Wraps rather than truncates, and carries a title. An IP is the one
              value here where losing the last digits is worse than an uneven
              card height: it is what every bot-IP warning is keyed on, and
              "208.67.222…" is indistinguishable from a different machine on the
              same subnet. */}
          <dd className="dev-num dev-facts-ip" title={a.botIp || ""}>
            {a.botIp || "—"}
          </dd>
        </div>
        <div>
          <dt>Usages</dt>
          <dd className="dev-num">{nf(a.usageCount)}</dd>
        </div>
      </dl>

      <div className="dev-flags">
        <span className={`dev-flag${a.hasPassword ? " dev-flag-on" : ""}`}>
          {a.hasPassword ? <CircleCheck size={12} /> : <X size={12} />}
          password stored
        </span>
        <span className={`dev-flag${a.lastTestOk ? " dev-flag-on" : ""}`}>
          {a.lastTestOk ? <CircleCheck size={12} /> : <X size={12} />}
          connection tested
        </span>
      </div>

      {a.missing.length > 0 ? (
        <p className="dev-missing">
          <TriangleAlert size={13} aria-hidden="true" />
          <span>
            Missing before it can go live: <strong>{a.missing.join(", ")}</strong>
          </span>
        </p>
      ) : (
        <p className="dev-ready">
          <CircleCheck size={13} aria-hidden="true" />
          <span>Ready for a live pair</span>
        </p>
      )}
    </article>
  );
}

/**
 * A card that opens a detail view, with an optional delete in the corner.
 *
 * The delete button sits OUTSIDE the big open-button rather than inside it —
 * nesting a button in a button is invalid and, in practice, means one of them
 * stops working. Absolute positioning puts it over the corner without being a
 * descendant.
 */
function OpenCard({ onOpen, children, del }) {
  return (
    <article className={`dev-card dev-card-click${del ? " dev-card-has-del" : ""}`}>
      <button type="button" className="dev-card-open" onClick={onOpen}>
        {children}
      </button>
      {del && <span className="dev-card-del">{del}</span>}
    </article>
  );
}

const DeveloperAccounts = () => {
  const navigate = useNavigate();
  const { dark, toggle } = useDevTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  /* When applying a pair is refused because an account is still recording in
     another container, the route returns WHICH container. Kept so the error can
     offer the way out instead of only naming the obstacle. */
  const [releaseHint, setReleaseHint] = useState(null);
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState("");
  /**
   * EDITING A MACHINE, and adding one.
   *
   * `editSrv` holds the id being edited and the DRAFT values, not a pointer into
   * `data` — a draft that read from `data` would be overwritten mid-typing by the
   * silent reload that follows every other action on this page.
   */
  const [editSrv, setEditSrv] = useState(null); // { id, ip, name }
  const [addSrv, setAddSrv] = useState(null); // { ip, name }

  /* null = the overview. Otherwise {kind: "broker"|"user"|"server", id}. */
  const [open, setOpen] = useState(null);

  /* The account form: {mode: "new"|"edit"|"old", account?}. */
  const [form, setForm] = useState(null);

  /* The current-pair editor. null = locked. */
  const [pairEdit, setPairEdit] = useState(null);
  const [applying, setApplying] = useState(false);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  }, []);

  const token = localStorage.getItem("token");
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  /**
   * WHICH CONTAINER THIS PAGE IS SHOWING.
   *
   * A container is a complete book: this page keeps every card, every count and
   * every detailed export it already had, and the dropdown only says whose
   * history fills them. "All" is every container together, which is what this
   * page showed before containers existed.
   *
   * Held in the URL — the choice is per viewer, a container becomes a link that
   * can be sent, and a reload comes back where it was. Empty means "let the
   * server pick", and it picks the DEFAULT container, never All.
   */
  const [container, setContainer] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("container") || "";
  });

  const chooseContainer = useCallback((next) => {
    setContainer(next);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (next) url.searchParams.set("container", next);
    else url.searchParams.delete("container");
    window.history.replaceState({}, "", url);
  }, []);


  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      setError("");
    setReleaseHint(null);
      try {
        const res = await fetch(
          `/api/dev/accounts/overview${container ? `?container=${encodeURIComponent(container)}` : ""}`,
          { headers: auth }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || body.error || `request failed (${res.status})`);
        }
        setData(await res.json());
      } catch (err) {
        setError(err.message || "could not load the account configuration");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    /* `container` is a dependency: without it every reload after a write would
       fetch the container this callback was built with, so switching would move
       the dropdown and leave the accounts, the totals and the period editor
       describing the previous one. */
    [auth, container]
  );

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };

  /**
   * Delete a user or a bot server.
   *
   * The page's own disabled state is a courtesy; this still reads the server's
   * refusal, because the page could be seconds out of date and the count that
   * matters is the one in the database.
   */
  const remove = useCallback(
    async (kind, id, name) => {
      setBusy(id);
      setError("");
      setNotice("");
      try {
        const res = await fetch(`/api/dev/accounts/${kind}/${id}`, {
          method: "DELETE",
          headers: auth,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || body.error || `failed (${res.status})`);
        setNotice(`Deleted ${name}.`);
        /* If the thing being viewed is the thing just deleted, go back — a detail
           view of a record that no longer exists would render as empty and look
           like a loading failure. */
        if (open && open.id === id) setOpen(null);
        load({ silent: true });
      } catch (err) {
        setError(err.message);
      } finally {
        setBusy("");
      }
    },
    [auth, load, open]
  );

  /**
   * Create or edit a bot machine.
   *
   * Two verbs, one function, because the difference is only which URL: POST
   * /servers records a machine by IP, PUT /servers/:id edits the record. Both
   * end the same way — close the form, say what happened, reload — and splitting
   * them would be two copies of that ending to keep in step.
   *
   * The reload is what makes the edit appear in the account form's dropdown as
   * well, since both read the same `overview` payload.
   */
  const saveServer = useCallback(
    async (id, ip, name) => {
      const addr = String(ip || "").trim();
      if (!addr) {
        setError("An IP address is required.");
        return;
      }
      setBusy(id || "new-server");
      setError("");
      setNotice("");
      try {
        const res = await fetch(id ? `/api/dev/accounts/servers/${id}` : "/api/dev/accounts/servers", {
          method: id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json", ...auth },
          body: JSON.stringify({ ip: addr, name: String(name || "").trim() }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || body.error || `failed (${res.status})`);
        const sv = body.server || {};
        setEditSrv(null);
        setAddSrv(null);
        setNotice(
          id
            ? `Saved ${sv.ip}${sv.name ? ` (${sv.name})` : ""}` +
              /* Said out loud, because correcting an IP moves every account on
                 that machine at once and that is the point of the record — but
                 it is not visible from the row that was edited. */
              (sv.accounts ? ` — ${sv.accounts} account(s) run there.` : ".")
            : `Recorded ${sv.ip}${sv.name ? ` (${sv.name})` : ""}.`
        );
        load({ silent: true });
      } catch (err) {
        setError(err.message);
      } finally {
        setBusy("");
      }
    },
    [auth, load]
  );

  /**
   * Apply the pair.
   *
   * The server does the ordering that matters — validate, close the outgoing
   * usage, save, recycle the poller — so this reports what happened. The number
   * of usages closed is shown deliberately: it is the difference between a clean
   * swap and the state that left three accounts open under one pair date.
   */
  const applyPair = async () => {
    if (!pairEdit) return;
    setApplying(true);
    setError("");
    setNotice("");
    try {
      /* THE CONTAINER GOES WITH THE SAVE. Without it the server falls back to
         the default, so editing Container 2's period here would write the date,
         the time and both account ids onto Container 1 — rotating the live
         container's period while this page reported success for the other. */
      const res = await fetch(
        `/api/dev/accounts/runtime${container ? `?container=${encodeURIComponent(container)}` : ""}`,
        {
        method: "PUT",
        headers: { ...auth, "Content-Type": "application/json" },
        body: JSON.stringify({
          pairStartDate: pairEdit.pairStartDate,
          pairStartTime: pairEdit.pairStartTime || "",
          accountIds: [pairEdit.a, pairEdit.b],
        }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body.details
          ? ` — ${body.details.map((d) => `${d.login} needs ${d.missing.join(", ")}`).join("; ")}`
          : "";
        /* The shared-account refusal comes with the containers that have to
           release the login first. Held for the error block below. */
        setReleaseHint(Array.isArray(body.release) && body.release.length ? body.release : null);
        throw new Error(`${body.message || body.error || "failed"}${detail}`);
      }
      setReleaseHint(null);

      const closed = body.closedUsages && body.closedUsages.closed;
      setNotice(
        [
          `Pair set to ${body.logins.join(" + ")} under ${body.pairStartDate} ` +
            `at ${body.pairStartTime || "00:00:00"}.`,
          closed
            ? `Closed ${closed} usage that left the pair (${body.closedUsages.usageIds.join(", ")}).`
            : "",
          body.applied && body.applied.ok
            ? "The poller was recycled — the server was not restarted."
            : `Not applied to a poller: ${(body.applied && body.applied.error) || "unknown"}`,
        ]
          .filter(Boolean)
          .join(" ")
      );
      setPairEdit(null);
      load({ silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  /** What is being viewed, resolved from `open` against the current data. */
  const opened = useMemo(() => {
    if (!data || !open) return null;
    if (open.kind === "broker") {
      const b = data.brokers.find((x) => x.id === open.id);
      return b && { kind: "broker", id: b.id, title: b.name, broker: b };
    }
    if (open.kind === "user") {
      const u = data.users.find((x) => x.id === open.id);
      return u && { kind: "user", id: u.id, title: u.name, user: u };
    }
    if (open.kind === "server") {
      const s = data.servers.find((x) => x.id === open.id);
      return s && { kind: "server", id: s.id, title: s.ip, server: s };
    }
    return null;
  }, [data, open]);

  /**
   * Bring a section into view, optionally narrowing the list on the way.
   *
   * The search term is set BEFORE the scroll and the scroll is deferred a frame,
   * because filtering changes the height of everything above the target — scroll
   * first and the section has moved by the time the browser gets there.
   *
   * `scrollIntoView` rather than a hash link: a hash would put the id in the URL
   * and a reload would then jump past the overview, which is not what a filter
   * button means.
   */
  const reveal = useCallback((id, term) => {
    if (term !== undefined) setQuery(term);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const reduce =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    });
  }, []);

  /* Filtering. Inside a detail view it narrows that scope's accounts; on the
     overview it narrows everything. */
  const view = useMemo(() => {
    if (!data) return { accounts: [], brokers: [], servers: [], users: [], active: false };
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const active = terms.length > 0;

    let scoped = data.accounts;
    if (opened) {
      if (opened.kind === "broker") scoped = scoped.filter((a) => a.brokerId === opened.id);
      if (opened.kind === "user") scoped = scoped.filter((a) => a.userId === opened.id);
      if (opened.kind === "server") scoped = scoped.filter((a) => a.botServerId === opened.id);
    }

    const accounts = scoped.filter(
      (a) =>
        !active ||
        matches(
          hay([
            a.login,
            a.brokerName,
            a.userName,
            a.host,
            a.port,
            a.botIp,
            a.botServerName,
            a.currency,
            a.leverage,
            a.platform,
            a.inLivePair ? "live current pair" : "not live",
            a.ready ? "ready" : "incomplete",
            ...a.missing,
            ...a.pairDates,
          ]),
          terms
        )
    );

    const shownBrokers = new Set(accounts.map((a) => a.brokerId));
    const shownServers = new Set(accounts.map((a) => a.botServerId).filter(Boolean));
    const shownUsers = new Set(accounts.map((a) => a.userId).filter(Boolean));

    return {
      accounts,
      brokers: data.brokers.filter((b) => !active || shownBrokers.has(b.id)),
      servers: data.servers.filter((s) => !active || shownServers.has(s.id)),
      users: data.users.filter((u) => !active || shownUsers.has(u.id)),
      active,
    };
  }, [data, query, opened]);

  const t = data && data.totals;
  const poller = data && data.poller;
  /* A poller that is running, but for a DIFFERENT container. There is one today
     and one per container after step 4; either way, this is what lets the panel
     say where it actually is instead of implying a fault. */
  const elsewhere = data && !poller && (data.pollers || []).length ? data.pollers[0] : null;

  /* Stored versus running, compared on SORTED logins: panel order is a display
     concern, not a disagreement.

     DRIFT IS ONLY MEANINGFUL WHEN THE POLLER IS THIS CONTAINER'S. `poller` is
     null otherwise, so this can no longer compare one container's stored config
     against another's running one — which it did, raising the warning for every
     container except the polled one. A false alarm on this particular warning is
     worse than none, because it is the one that matters when it is real. */
  const sameList = (a, b) =>
    (a || []).slice().sort().join(",") === (b || []).slice().sort().join(",");
  const drift =
    data &&
    data.runtime &&
    poller &&
    poller.up &&
    (!sameList(data.runtime.logins, poller.logins) ||
      (data.runtime.pairStartDate !== poller.pairStartDate ||
        (data.runtime.pairStartTime || "00:00:00") !== (poller.pairStartTime || "00:00:00")));

  const eligible = data ? data.accounts.filter((a) => a.ready) : [];

  const openIt = (kind, id) => {
    setOpen({ kind, id });
    setQuery("");
  };

  return (
    <div className={`dev-root dev-theme-${dark ? "dark" : "light"}`}>
      <div className="dev-glow" aria-hidden="true" />

      <header className="dev-topbar">
        <div className="dev-brand">
          <img src="/firstbench-logo.png" alt="FirstBench" className="dev-logo" />
          <div className="dev-titles">
            <h1 className="dev-title">{opened ? opened.title : "Accounts"}</h1>
            <p className="dev-sub">{user && user.username ? user.username : "signed in"}</p>
          </div>
        </div>

        <div className="dev-search">
          <Search size={15} aria-hidden="true" className="dev-search-icon" />
          <input
            type="search"
            className="dev-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setQuery("");
            }}
            placeholder={
              opened
                ? `Search inside ${opened.title} — user, login, host, bot IP…`
                : "Search login, broker, user, host, bot IP…"
            }
            aria-label="Filter the accounts below"
          />
          {query && (
            <>
              <span className="dev-search-count">{nf(view.accounts.length)}</span>
              <button
                type="button"
                className="dev-search-clear"
                onClick={() => setQuery("")}
                aria-label="Clear the search"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        <div className="dev-topbar-actions">
          {/* First in the bar, as on the dashboard: it governs the accounts
              listed, every total, and which container the period editor writes
              to. */}
          <ContainerSelect
            containers={(data && data.containers) || []}
            value={
              data && data.selection
                ? data.selection.mode === "all"
                  ? "all"
                  : data.selection.containerId || ""
                : container
            }
            onChange={chooseContainer}
            includeAll
            label="Container"
            id="dev-acc-container"
            busy={loading}
          />

          <Link to="/dev/containers" className="dev-ghost-btn" title="Create and edit containers">
            <Layers size={15} aria-hidden="true" />
            <span>Containers</span>
          </Link>
          <Link
            to={`/dev/dashboard${container ? `?container=${encodeURIComponent(container)}` : ""}`}
            className="dev-ghost-btn"
            title="Downloads and history"
          >
            <LayoutDashboard size={15} aria-hidden="true" />
            <span>Dashboard</span>
          </Link>
          <button
            type="button"
            className="dev-icon-btn"
            onClick={toggle}
            aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
          >
            {dark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
          </button>
          <button
            type="button"
            className="dev-icon-btn"
            onClick={() => load()}
            disabled={loading}
            aria-label="Reload"
          >
            <RefreshCw size={16} aria-hidden="true" className={loading ? "dev-spin" : ""} />
          </button>
          <button type="button" className="dev-ghost-btn" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </header>

      <main className="dev-main">
        {error && (
          <div className="dev-error" role="alert">
            <TriangleAlert size={16} aria-hidden="true" />
            <div>
              <span>{error}</span>
              {/* THE WAY OUT, not just the obstacle. An account can only be
                  recorded by one container at a time, and the fix is to release
                  it from the one that holds it — so link straight there rather
                  than leaving the reader to work out where to go. */}
              {releaseHint && (
                <p className="dev-help" style={{ marginTop: 6 }}>
                  Release{" "}
                  {releaseHint.map((h, i) => (
                    <React.Fragment key={`${h.containerId}-${h.login}`}>
                      {i > 0 ? ", " : ""}
                      <b>{h.login}</b> from <b>{h.containerName || h.containerId}</b>
                    </React.Fragment>
                  ))}{" "}
                  on <Link to="/dev/containers">the container books page</Link>, then set this period
                  again.
                </p>
              )}
            </div>
          </div>
        )}
        {notice && (
          <div className="dev-info" role="status">
            <CircleCheck size={16} aria-hidden="true" />
            <div>{notice}</div>
          </div>
        )}

        {loading && !data && <p className="dev-note">Reading the account configuration…</p>}

        {data && !data.stage.secretKeySet && (
          <p className="dev-warn">
            <KeyRound size={15} aria-hidden="true" />
            <span>
              <code>ACCOUNT_SECRET_KEY</code> is not set in this process, so stored passwords cannot
              be decrypted and none can be added.
            </span>
          </p>
        )}

        {/* ====================== A DETAIL VIEW ====================== */}
        {data && opened && (
          <>
            <div className="dev-crumb">
              <button type="button" className="dev-ghost-btn" onClick={() => setOpen(null)}>
                <ArrowLeft size={15} aria-hidden="true" />
                <span>Back</span>
              </button>
              <span className="dev-crumb-text dev-mono-sm">
                {opened.kind === "broker" &&
                  (opened.broker.hosts.map((h) => `${h.host}:${h.port}`).join("  ·  ") ||
                    "no host recorded")}
                {opened.kind === "user" &&
                  (opened.user.brokers.join("  ·  ") || "no broker yet")}
                {opened.kind === "server" && (opened.server.name || "unnamed machine")}
              </span>
              {opened.kind === "user" && (
                <DeleteButton
                  label="user"
                  inUse={opened.user.accounts}
                  busy={busy === opened.id}
                  onDelete={() => remove("users", opened.id, opened.user.name)}
                />
              )}
              {opened.kind === "server" && (
                <>
                  <button
                    type="button"
                    className="dev-ghost-btn dev-ghost-btn-sm"
                    onClick={() =>
                      setEditSrv({
                        id: opened.id,
                        ip: opened.server.ip,
                        name: opened.server.name || "",
                      })
                    }
                  >
                    <Pencil size={13} aria-hidden="true" />
                    <span>Edit</span>
                  </button>
                  <DeleteButton
                    label="bot server"
                    inUse={opened.server.accounts.length}
                    busy={busy === opened.id}
                    onDelete={() => remove("servers", opened.id, opened.server.ip)}
                  />
                </>
              )}
            </div>

            {/* --- who/what else is involved, one section per kind --- */}
            {opened.kind === "broker" && (
              <section className="dev-section">
                <div className="dev-section-head">
                  <h2 className="dev-section-title">
                    <span className="dev-section-icon" aria-hidden="true">
                      <Users size={15} />
                    </span>
                    Users at this broker
                    <span className="dev-section-count">{nf(opened.broker.userCount)}</span>
                  </h2>
                  <p className="dev-section-note">
                    The search above is scoped to this broker — type a bot IP to see which of these
                    people run on that machine, and which accounts.
                  </p>
                </div>
                {opened.broker.users.length === 0 ? (
                  <p className="dev-empty">
                    No account here has a user yet — {nf(opened.broker.unassigned)} unassigned.
                  </p>
                ) : (
                  <div className="dev-grid">
                    {opened.broker.users.map((name) => {
                      const mine = view.accounts.filter((a) => a.userName === name);
                      if (view.active && mine.length === 0) return null;
                      const u = data.users.find((x) => x.name === name);
                      const ips = [...new Set(mine.map((a) => a.botIp).filter(Boolean))];
                      return (
                        <OpenCard key={name} onOpen={() => u && openIt("user", u.id)}>
                          <div className="dev-card-head">
                            <h3 className="dev-card-title" title={name}>
                              {name}
                            </h3>
                            <span className="dev-tag">{mine.length} here</span>
                          </div>
                          <p className="dev-card-text dev-mono-sm">
                            {mine.map((a) => a.login).join("  ·  ") || "—"}
                          </p>
                          <p className="dev-card-when">
                            {ips.length ? `bot IP: ${ips.join("  ·  ")}` : "no bot IP recorded"}
                          </p>
                        </OpenCard>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {opened.kind === "user" && (
              <section className="dev-section">
                <div className="dev-section-head">
                  <h2 className="dev-section-title">
                    <span className="dev-section-icon" aria-hidden="true">
                      <Building2 size={15} />
                    </span>
                    Brokers this person holds accounts at
                    <span className="dev-section-count">{nf(opened.user.brokers.length)}</span>
                  </h2>
                  <p className="dev-section-note">
                    One user record spans brokers, which is what lets the bot-IP check tell "the
                    same person at another broker" (fine) from "a different person at this broker"
                    (a warning).
                  </p>
                </div>
                <div className="dev-grid">
                  {opened.user.brokers.map((name) => {
                    const mine = view.accounts.filter((a) => a.brokerName === name);
                    if (view.active && mine.length === 0) return null;
                    const b = data.brokers.find((x) => x.name === name);
                    const ips = [...new Set(mine.map((a) => a.botIp).filter(Boolean))];
                    return (
                      <OpenCard key={name} onOpen={() => b && openIt("broker", b.id)}>
                        <div className="dev-card-head">
                          <h3 className="dev-card-title" title={name}>
                            {name}
                          </h3>
                          <span className="dev-tag">{mine.length} account(s)</span>
                        </div>
                        <p className="dev-card-text dev-mono-sm">
                          {mine.map((a) => a.login).join("  ·  ") || "—"}
                        </p>
                        <p className="dev-card-when">
                          {ips.length ? `bot IP: ${ips.join("  ·  ")}` : "no bot IP recorded"}
                        </p>
                      </OpenCard>
                    );
                  })}
                </div>
              </section>
            )}

            {opened.kind === "server" && (
              <section className="dev-section">
                <div className="dev-section-head">
                  <h2 className="dev-section-title">
                    <span className="dev-section-icon" aria-hidden="true">
                      <Server size={15} />
                    </span>
                    Who runs on this machine
                    <span className="dev-section-count">{nf(opened.server.userCount)}</span>
                  </h2>
                  <p className="dev-section-note">
                    Nothing connects through this IP — it is recorded so a machine shared by two
                    different people at the SAME broker can be noticed.
                  </p>
                </div>
                {opened.server.brokerCount === 1 && opened.server.userCount > 1 && (
                  <p className="dev-warn">
                    <TriangleAlert size={15} aria-hidden="true" />
                    <span>
                      Shared by more than one person at a single broker — exactly the case the
                      bot-IP warning exists for.
                    </span>
                  </p>
                )}
                <div className="dev-grid">
                  {[...new Set(view.accounts.map((a) => a.userName || "(no user)"))].map((name) => {
                    const mine = view.accounts.filter((a) => (a.userName || "(no user)") === name);
                    const u = data.users.find((x) => x.name === name);
                    return (
                      <OpenCard key={name} onOpen={() => u && openIt("user", u.id)}>
                        <div className="dev-card-head">
                          <h3 className="dev-card-title" title={name}>
                            {name}
                          </h3>
                          <span className="dev-tag">{mine.length} account(s)</span>
                        </div>
                        <p className="dev-card-text">
                          {[...new Set(mine.map((a) => a.brokerName))].join("  ·  ")}
                        </p>
                        <p className="dev-card-when dev-mono-sm">
                          {mine.map((a) => a.login).join("  ·  ")}
                        </p>
                      </OpenCard>
                    );
                  })}
                </div>
              </section>
            )}

            {/* --- the accounts in this scope --- */}
            <section className="dev-section">
              <div className="dev-section-head dev-section-head-row">
                <h2 className="dev-section-title">
                  <span className="dev-section-icon" aria-hidden="true">
                    <Database size={15} />
                  </span>
                  Accounts
                  <span className={`dev-section-count${view.active ? " dev-section-count-on" : ""}`}>
                    {nf(view.accounts.length)}
                  </span>
                </h2>
                <button type="button" className="dev-btn" onClick={() => setForm({ mode: "new" })}>
                  <Plus size={13} aria-hidden="true" />
                  <span>Add account</span>
                </button>
              </div>

              {view.accounts.length === 0 && (
                <p className="dev-empty">Nothing here matches this search.</p>
              )}
              <div className="dev-grid">
                {view.accounts.map((a) => (
                  <AccountCard
                    key={a.id}
                    a={a}
                    onEdit={(acc) => setForm({ mode: "edit", account: acc })}
                  />
                ))}
              </div>
            </section>
          </>
        )}

        {/* ========================= THE OVERVIEW ========================= */}
        {data && !opened && (
          <>
            <section className="dev-section">
              <div className="dev-section-head">
                <h2 className="dev-section-title">
                  <span className="dev-section-icon" aria-hidden="true">
                    <Database size={15} />
                  </span>
                  Overview
                </h2>
              </div>

              <div className="dev-tiles">
                <Tile n={t.brokers} label="brokers" go={() => reveal("dev-sec-brokers")} />
                <Tile n={t.users} label="users" go={() => reveal("dev-sec-users")} />
                <Tile n={t.servers} label="bot servers" go={() => reveal("dev-sec-servers")} />
                <Tile n={t.accounts} label="accounts" go={() => reveal("dev-sec-accounts")} />
                <Tile
                  n={t.ready}
                  label="ready for a live pair"
                  tone={t.ready ? "ok" : null}
                  go={() => reveal("dev-sec-accounts", "ready")}
                />
                <Tile
                  n={t.incomplete}
                  label="incomplete"
                  tone={t.incomplete ? "warn" : null}
                  go={() => reveal("dev-sec-accounts", "incomplete")}
                />
              </div>

              <div className="dev-pairgrid">
                <div className="dev-card">
                  <div className="dev-card-head">
                    <h3 className="dev-card-title">Current pair</h3>
                    {!pairEdit ? (
                      <button
                        type="button"
                        className="dev-icon-btn dev-icon-btn-sm"
                        onClick={() =>
                          setPairEdit({
                            pairStartDate: (data.runtime && data.runtime.pairStartDate) || "",
                            pairStartTime: (data.runtime && data.runtime.pairStartTime) || "",
                            a: (data.runtime && data.runtime.accountIds[0]) || "",
                            b: (data.runtime && data.runtime.accountIds[1]) || "",
                          })
                        }
                        aria-label="Edit the current pair"
                        title="Edit"
                      >
                        <Pencil size={13} aria-hidden="true" />
                      </button>
                    ) : (
                      <span className="dev-badge dev-badge-live">editing</span>
                    )}
                  </div>

                  {!pairEdit ? (
                    data.runtime ? (
                      <>
                        <p className="dev-card-text">
                          Pair start time{" "}
                          <span className="dev-num">
                            {data.runtime.pairStartTime || "00:00:00"}
                          </span>
                        </p>
                        <p className="dev-card-text">
                          Pair start date{" "}
                          <span className="dev-num">{data.runtime.pairStartDate || "—"}</span>
                        </p>
                        <p className="dev-card-text dev-mono">
                          {data.runtime.logins.length
                            ? data.runtime.logins.join("  +  ")
                            : "no accounts set"}
                        </p>
                        <p className="dev-card-when">
                          set by {data.runtime.updatedBy || "—"} ·{" "}
                          {data.runtime.appliedAt ? "applied to the poller" : "never applied"}
                        </p>
                      </>
                    ) : (
                      <p className="dev-card-text">
                        Nothing stored yet. Run{" "}
                        <code>node scripts/accounts-import.js --write</code>, or set a pair here.
                      </p>
                    )
                  ) : (
                    <>
                      <div className="dev-field">
                        <label htmlFor="pe-date">Pair start date</label>
                        <input
                          id="pe-date"
                          type="date"
                          value={pairEdit.pairStartDate}
                          onChange={(e) =>
                            setPairEdit({ ...pairEdit, pairStartDate: e.target.value })
                          }
                        />
                        <p className="dev-help">
                          Shown in your local format; stored and sent as YYYY-MM-DD.
                        </p>
                      </div>
                      <div className="dev-field">
                        <label htmlFor="pe-time">Pair start time</label>
                        <input
                          id="pe-time"
                          type="time"
                          step="1"
                          value={pairEdit.pairStartTime}
                          onChange={(e) =>
                            setPairEdit({ ...pairEdit, pairStartTime: e.target.value })
                          }
                        />
                        {/* The reason this field exists, stated where the decision is
                            made rather than left to be rediscovered: a withdrawal is
                            taken in the middle of a trading day, and rotating at
                            midnight of that date puts the morning's trades on both
                            sides of the boundary. Set it just AFTER the withdrawal —
                            the window is [start, end), so a deal exactly ON the
                            boundary falls into the NEW pair, and a withdrawal left
                            there stops banking the old pair's profit. */}
                        <p className="dev-help">
                          Leave blank for midnight. If you withdrew part-way through
                          the day, set this one second after the last withdrawal —
                          trades before it stay with the pair they were earned in,
                          trades from it onward start the new pair.
                        </p>
                      </div>
                      <div className="dev-field">
                        <label htmlFor="pe-a">Account 1</label>
                        <select
                          id="pe-a"
                          value={pairEdit.a}
                          onChange={(e) => setPairEdit({ ...pairEdit, a: e.target.value })}
                        >
                          <option value="">— choose —</option>
                          {eligible.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.login} — {a.brokerName} — {a.userName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="dev-field">
                        <label htmlFor="pe-b">Account 2</label>
                        <select
                          id="pe-b"
                          value={pairEdit.b}
                          onChange={(e) => setPairEdit({ ...pairEdit, b: e.target.value })}
                        >
                          <option value="">— choose —</option>
                          {eligible.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.login} — {a.brokerName} — {a.userName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <p className="dev-help">
                        Updating applies immediately: any account leaving the pair has its open usage
                        closed, then the poller child is recycled. The server is not restarted.
                        {eligible.length < 2 &&
                          " Only accounts with every detail and a successful test are listed, and fewer than two are ready."}
                      </p>

                      <div className="dev-modal-actions">
                        <button
                          type="button"
                          className="dev-ghost-btn"
                          onClick={() => setPairEdit(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="dev-btn"
                          onClick={applyPair}
                          disabled={
                            applying ||
                            !pairEdit.pairStartDate ||
                            !pairEdit.a ||
                            !pairEdit.b ||
                            pairEdit.a === pairEdit.b
                          }
                        >
                          {applying ? "Applying…" : "Update and apply"}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div className="dev-card">
                  <div className="dev-card-head">
                    <h3 className="dev-card-title">What is polling this container</h3>
                    {poller && poller.up ? (
                      <span className="dev-badge dev-badge-live">up</span>
                    ) : (
                      <span className="dev-badge">not polled</span>
                    )}
                  </div>
                  {poller ? (
                    <>
                      <p className="dev-card-text">
                        Pair start date <span className="dev-num">{poller.pairStartDate || "—"}</span>
                      </p>
                      <p className="dev-card-text">
                        Pair start time{" "}
                        <span className="dev-num">{poller.pairStartTime || "00:00:00"}</span>
                      </p>
                      <p className="dev-card-text dev-mono">
                        {poller.logins && poller.logins.length ? poller.logins.join("  +  ") : "none"}
                      </p>
                      <p className="dev-card-when">
                        config from{" "}
                        <strong>
                          {poller.source === "db" ? "the database" : poller.source || "—"}
                        </strong>
                        {poller.reloadedAt ? " · reloaded without a restart" : ""}
                        {poller.restarts ? ` · ${poller.restarts} restart(s)` : ""}
                      </p>
                    </>
                  ) : elsewhere ? (
                    /* THE IMPORTANT CASE. A poller IS running — just not this
                       container. Saying only "not reporting" would read as a
                       fault, and showing that poller's logins here would be a
                       lie: they belong to another book. */
                    <>
                      <p className="dev-card-text">
                        Nothing is polling this container. The poller is running{" "}
                        <strong>{elsewhere.containerName || "another container"}</strong>.
                      </p>
                      <p className="dev-card-when">
                        Its figures on this page are the last ones stored. Make this container the
                        default, or wait for its own poller.
                      </p>
                    </>
                  ) : (
                    <p className="dev-card-text">No poller is reporting at all.</p>
                  )}
                </div>
              </div>

              {drift && (
                <p className="dev-warn">
                  <TriangleAlert size={15} aria-hidden="true" />
                  <span>
                    What is stored and what is running do not agree. The poller is the truth about
                    what is trading — open the pair with the pencil and update to apply the stored
                    one.
                  </span>
                </p>
              )}
            </section>

            {/* ------------------------------- brokers -------------------------- */}
            <section className="dev-section" id="dev-sec-brokers">
              <div className="dev-section-head dev-section-head-row">
                <h2 className="dev-section-title">
                  <span className="dev-section-icon" aria-hidden="true">
                    <Building2 size={15} />
                  </span>
                  Brokers
                  <span className={`dev-section-count${view.active ? " dev-section-count-on" : ""}`}>
                    {view.active
                      ? `${nf(view.brokers.length)} of ${nf(data.brokers.length)}`
                      : nf(data.brokers.length)}
                  </span>
                </h2>
                <div className="dev-modal-actions">
                  <button
                    type="button"
                    className="dev-ghost-btn"
                    onClick={() => setForm({ mode: "old" })}
                  >
                    <Plus size={14} aria-hidden="true" />
                    <span>Add old account</span>
                  </button>
                  <button type="button" className="dev-btn" onClick={() => setForm({ mode: "new" })}>
                    <Plus size={13} aria-hidden="true" />
                    <span>Add account</span>
                  </button>
                </div>
              </div>
              <p className="dev-section-note">
                Open a broker, a person or a machine to see its accounts and search inside it.
              </p>

              {view.brokers.length === 0 && (
                <p className="dev-empty">No brokers match this search.</p>
              )}

              <div className="dev-grid">
                {view.brokers.map((b) => (
                  <OpenCard key={b.id} onOpen={() => openIt("broker", b.id)}>
                    <div className="dev-card-head">
                      <h3 className="dev-card-title" title={b.name}>
                        {b.name}
                      </h3>
                      <span className="dev-tag">
                        {b.source === "broker" ? "from broker" : "typed"}
                      </span>
                    </div>
                    <p className="dev-card-text dev-mono-sm">
                      {b.hosts.map((h) => `${h.host}:${h.port}`).join("  ·  ") || "no host recorded"}
                    </p>
                    <dl className="dev-facts">
                      <div>
                        <dt>Accounts</dt>
                        <dd className="dev-num">{nf(b.accountCount)}</dd>
                      </div>
                      <div>
                        <dt>Users</dt>
                        <dd className="dev-num">{nf(b.userCount)}</dd>
                      </div>
                      <div>
                        <dt>No user</dt>
                        <dd className="dev-num">{nf(b.unassigned)}</dd>
                      </div>
                    </dl>
                    {b.users.length > 0 && <p className="dev-card-when">{b.users.join("  ·  ")}</p>}
                  </OpenCard>
                ))}
              </div>
            </section>

            {/* -------------------------------- users --------------------------- */}
            <section className="dev-section" id="dev-sec-users">
              <div className="dev-section-head">
                <h2 className="dev-section-title">
                  <span className="dev-section-icon" aria-hidden="true">
                    <Users size={15} />
                  </span>
                  Users
                  <span className={`dev-section-count${view.active ? " dev-section-count-on" : ""}`}>
                    {view.active
                      ? `${nf(view.users.length)} of ${nf(data.users.length)}`
                      : nf(data.users.length)}
                  </span>
                </h2>
                <p className="dev-section-note">
                  One record per person, spanning brokers. Open one to see its accounts. Deleting is
                  possible only while no account uses it — the bin is disabled otherwise, and the
                  server refuses regardless.
                </p>
              </div>

              {data.users.length === 0 ? (
                <p className="dev-empty">
                  No users yet, so all {nf(t.accounts)} accounts are unassigned and none can go live.
                </p>
              ) : (
                <div className="dev-grid">
                  {view.users.map((u) => (
                    <OpenCard
                      key={u.id}
                      onOpen={() => openIt("user", u.id)}
                      del={
                        <DeleteButton
                          label="user"
                          inUse={u.accounts}
                          busy={busy === u.id}
                          onDelete={() => remove("users", u.id, u.name)}
                        />
                      }
                    >
                      <div className="dev-card-head">
                        <h3 className="dev-card-title" title={u.name}>
                          {u.name}
                        </h3>
                        <span className="dev-tag">{u.accounts} account(s)</span>
                      </div>
                      <p className="dev-card-text">{u.brokers.join("  ·  ") || "no broker yet"}</p>
                      {u.note && <p className="dev-card-when">{u.note}</p>}
                    </OpenCard>
                  ))}
                </div>
              )}
            </section>

            {/* ----------------------------- bot servers ------------------------ */}
            <section className="dev-section" id="dev-sec-servers">
              <div className="dev-section-head dev-section-head-row">
                <h2 className="dev-section-title">
                  <span className="dev-section-icon" aria-hidden="true">
                    <Server size={15} />
                  </span>
                  Bot servers
                  <span className={`dev-section-count${view.active ? " dev-section-count-on" : ""}`}>
                    {view.active
                      ? `${nf(view.servers.length)} of ${nf(data.servers.length)}`
                      : nf(data.servers.length)}
                  </span>
                </h2>
                <div className="dev-modal-actions">
                  <button
                    type="button"
                    className="dev-btn"
                    onClick={() => setAddSrv({ ip: "", name: "" })}
                    disabled={Boolean(addSrv)}
                  >
                    <Plus size={13} aria-hidden="true" />
                    <span>Add server</span>
                  </button>
                </div>
              </div>
              <p className="dev-section-note">
                One label per IP, shared by every account running there. Open one to see who runs on
                it. Renaming or correcting an IP moves every account on that machine at once —
                accounts point at the record, never at the address. Deleting is possible only while
                no account uses it.
              </p>

              {addSrv && (
                <div className="dev-inline-add">
                  <input
                    type="text"
                    value={addSrv.ip}
                    onChange={(e) => setAddSrv({ ...addSrv, ip: e.target.value })}
                    placeholder="IP address"
                    aria-label="New server IP"
                  />
                  <input
                    type="text"
                    value={addSrv.name}
                    onChange={(e) => setAddSrv({ ...addSrv, name: e.target.value })}
                    placeholder="label, e.g. VPS-1"
                    aria-label="New server label"
                  />
                  <button
                    type="button"
                    className="dev-btn dev-btn-sm"
                    onClick={() => saveServer(null, addSrv.ip, addSrv.name)}
                    disabled={!addSrv.ip.trim() || busy === "new-server"}
                  >
                    {busy === "new-server" ? "Saving…" : "Add"}
                  </button>
                  <button
                    type="button"
                    className="dev-ghost-btn dev-ghost-btn-sm"
                    onClick={() => setAddSrv(null)}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {data.servers.length === 0 ? (
                <p className="dev-empty">
                  No bot servers yet. Add one above, or record an IP while editing an account.
                </p>
              ) : (
                <div className="dev-grid">
                  {view.servers.map((s) => (
                    <OpenCard
                      key={s.id}
                      onOpen={() => openIt("server", s.id)}
                      del={
                        <DeleteButton
                          label="bot server"
                          inUse={s.accounts.length}
                          busy={busy === s.id}
                          onDelete={() => remove("servers", s.id, s.ip)}
                        />
                      }
                    >
                      <div className="dev-card-head">
                        <h3 className="dev-card-title dev-mono" title={s.ip}>
                          {s.ip}
                        </h3>
                        <span className="dev-tag">{s.name || "unnamed"}</span>
                      </div>
                      {/* NO EDIT BUTTON HERE, deliberately. OpenCard's whole
                          body is a <button> (dev-card-open), so a control inside
                          it is a button nested in a button - invalid, and the
                          reason the first attempt needed stopPropagation to work
                          at all. The slot that IS outside it is dev-card-del,
                          absolute in the corner, and the head reserves exactly
                          44px for it; the comment on that rule records that 96px
                          clipped the IP title, and the rule below it says an IP
                          must be readable in full. A second control there would
                          spend the title space that comment was written to
                          protect. The card opens the machine in one click, and
                          Edit lives in its header alongside Delete. */}
                      <dl className="dev-facts">
                        <div>
                          <dt>Accounts</dt>
                          <dd className="dev-num">{nf(s.accounts.length)}</dd>
                        </div>
                        <div>
                          <dt>Brokers</dt>
                          <dd className="dev-num">{nf(s.brokerCount)}</dd>
                        </div>
                        <div>
                          <dt>Users</dt>
                          <dd className="dev-num">{nf(s.userCount)}</dd>
                        </div>
                      </dl>
                      {s.accounts.slice(0, 4).map((a) => (
                        <p className="dev-card-when" key={a.login}>
                          {a.login} · {a.broker} · {a.user}
                        </p>
                      ))}
                      {s.brokerCount === 1 && s.userCount > 1 && (
                        <p className="dev-missing">
                          <TriangleAlert size={13} aria-hidden="true" />
                          <span>Shared by more than one person at a single broker.</span>
                        </p>
                      )}
                    </OpenCard>
                  ))}
                </div>
              )}
            </section>

            {/* ------------------------------ accounts -------------------------- */}
            <section className="dev-section" id="dev-sec-accounts">
              <div className="dev-section-head">
                <h2 className="dev-section-title">
                  <span className="dev-section-icon" aria-hidden="true">
                    <Database size={15} />
                  </span>
                  All accounts
                  <span className={`dev-section-count${view.active ? " dev-section-count-on" : ""}`}>
                    {view.active
                      ? `${nf(view.accounts.length)} of ${nf(data.accounts.length)}`
                      : nf(data.accounts.length)}
                  </span>
                </h2>
                <p className="dev-section-note">
                  An account is the credentials and identity; a usage is one window it traded in.
                  Login 7000103 is one account here and four usages on the dashboard — which is what
                  lets an old account be used again under a new pair date without a second record.
                </p>
              </div>

              {view.accounts.length === 0 && (
                <p className="dev-empty">No accounts match this search.</p>
              )}
              <div className="dev-grid">
                {view.accounts.map((a) => (
                  <AccountCard
                    key={a.id}
                    a={a}
                    onEdit={(acc) => setForm({ mode: "edit", account: acc })}
                  />
                ))}
              </div>
            </section>
          </>
        )}
      </main>

      {/* ------------------------- editing one machine ----------------------
          A MODAL, because Edit is offered from two places - the card in the Bot
          servers list and the open machine's own header - and one form serving
          both is one form to keep correct. `editSrv` carries the draft, so the
          silent reload that follows every action on this page cannot overwrite
          what is being typed. */}
      {editSrv && (
        <div className="dev-modal-back" role="dialog" aria-modal="true" aria-label="Edit bot server">
          <div className="dev-modal">
            <div className="dev-modal-head">
              <h2>Edit bot server</h2>
              <button type="button" className="dev-icon-btn" onClick={() => setEditSrv(null)} aria-label="Close">
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="dev-modal-body">
              <div className="dev-row">
                <div className="dev-field">
                  <label htmlFor="srv-ip">IP address</label>
                  <input
                    id="srv-ip"
                    type="text"
                    value={editSrv.ip}
                    onChange={(e) => setEditSrv({ ...editSrv, ip: e.target.value })}
                    placeholder="10.0.0.1"
                  />
                </div>
                <div className="dev-field">
                  <label htmlFor="srv-name">Label</label>
                  <input
                    id="srv-name"
                    type="text"
                    value={editSrv.name}
                    onChange={(e) => setEditSrv({ ...editSrv, name: e.target.value })}
                    placeholder="VPS-1"
                  />
                </div>
              </div>
              {/* Said before the edit, not after: the accounts are the reason a
                  machine is its own record, and correcting an address here is
                  the one action whose effect is invisible from this form. */}
              {(() => {
                const sv = (data && data.servers.find((x) => x.id === editSrv.id)) || null;
                if (!sv) return null;
                return (
                  <>
                    <p className="dev-hint">
                      {sv.accounts.length === 0
                        ? "No account runs here yet."
                        : `${nf(sv.accounts.length)} account(s) run here and will follow this change — ` +
                          "they point at the machine, not at the address."}
                    </p>
                    {sv.accounts.length > 0 && (
                      <ul className="dev-list">
                        {sv.accounts.map((a) => (
                          <li key={a.login}>
                            <span className="dev-mono">{a.login}</span> · {a.broker} · {a.user}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="dev-modal-foot">
              <button type="button" className="dev-ghost-btn" onClick={() => setEditSrv(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="dev-btn"
                onClick={() => saveServer(editSrv.id, editSrv.ip, editSrv.name)}
                disabled={!editSrv.ip.trim() || busy === editSrv.id}
              >
                {busy === editSrv.id ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {form && data && (
        <AccountForm
          mode={form.mode}
          account={form.account}
          brokers={data.brokers}
          users={data.users}
          /* Every bot machine known, for the two linked dropdowns in step 3.
             Already in this payload - accountConfig.overview returns `servers`
             with its accounts, which is what the bot-IP warning rules run on. */
          servers={data.servers || []}
          token={token}
          /* The container currently selected, resolved from the server's own
             answer rather than the raw URL parameter — so an empty parameter
             still creates into the default the page is actually showing. */
          container={(data.selection && data.selection.containerId) || container}
          onClose={() => setForm(null)}
          onSaved={({ silent }) => {
            load({ silent: true });
            if (!silent) setNotice("Saved.");
          }}
        />
      )}
    </div>
  );
};

export default DeveloperAccounts;
